/**
 * App root — wires providers (React Query, optionally Clerk) and the router.
 *
 * Clerk is mounted only when `VITE_CLERK_PUBLISHABLE_KEY` is provided. In
 * mock-only dev (the default) we skip it so the app boots without auth.
 */

import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import { AppRoutes } from "./routes";
import { isMockEnabled } from "./lib/mock-api";
import { ErrorBoundary } from "./components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function MaybeClerkProvider({ children }: { children: React.ReactNode }) {
  // In mock-only dev (no Clerk key), bypass Clerk so the app renders without
  // an auth context. The settings/auth UI guards itself.
  if (!CLERK_KEY || (isMockEnabled() && !CLERK_KEY)) return <>{children}</>;
  return <ClerkProvider publishableKey={CLERK_KEY}>{children}</ClerkProvider>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <MaybeClerkProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={(import.meta.env.BASE_URL || "/").replace(/\/$/, "") || undefined}>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </MaybeClerkProvider>
    </ErrorBoundary>
  );
}
